// ⚠️ FICHIER GÉNÉRÉ — ne pas éditer ici. Source : shared/ui/ · régénérer avec `bash shared/sync.sh`
'use client';
// Gommette « Déclarer une non-conformité » — présente sur TOUS les écrans des
// trois modules, au même endroit.
//
// Le problème qu'elle traite n'est pas l'ergonomie du formulaire de NC : c'est
// que rien ne se déclare. Un technicien qui constate un écart à 7 h 20 en
// pleine série ne va pas quitter son écran, chercher le module Qualité, trouver
// le registre, ouvrir un formulaire de quinze champs. Il se dit qu'il le fera
// plus tard, et « plus tard » n'existe pas. Le registre des NC d'un laboratoire
// n'est jamais trop rempli — il est presque toujours trop vide, et ce vide se
// paie en audit comme en récidive.
//
// D'où trois partis pris :
//
//  · TOUJOURS À PORTÉE. Un bouton flottant, même position partout. On ne
//    navigue pas pour déclarer : on déclare depuis là où on a vu.
//  · LE MÊME FORMULAIRE QU'AU REGISTRE (RQ, 05/10/2026). La gommette est un
//    RACCOURCI, pas un second formulaire : l'hôte injecte celui de la rubrique
//    « Non-conformités » (`formulaireNC`) et la fenêtre l'affiche tel quel.
//    Les deux champs d'avant — ce qui s'est passé, et où — tenaient le pari
//    qu'une déclaration pauvre valait mieux qu'aucune ; ils privaient surtout
//    le déclarant de la liste des problèmes de son secteur et de la question
//    des résultats patients, posées deux écrans plus loin. La fenêtre express
//    reste en REPLI, pour les hôtes sans ce formulaire (le catalogue public).
//  · LE CONTEXTE EST DÉJÀ SU. L'application sait de quel écran on déclare :
//    elle renseigne la source et la pièce concernée sans rien demander.
//
// ── Second canal : la SUGGESTION du personnel (§8.6, lot ISO-9) ─────────────
//
// Le §8.6 demande que l'amélioration se nourrisse aussi du retour du PERSONNEL.
// Le problème à résoudre est mot pour mot celui de la non-conformité : ce qui
// ne se dépose pas en vingt secondes ne se dépose jamais. Plutôt que construire
// un second mécanisme de dépôt, ce composant en accueille un second CANAL —
// même bouton flottant, même fenêtre, mêmes deux champs, un point de chute
// différent (`/api/suggestion` au lieu de `/api/nc`).
//
// Ce qui change entre les deux, et c'est tout ce qui change :
//
//  · le vocabulaire — on ne « déclare » pas une idée, on la propose ;
//  · l'ANONYMAT, offert à la suggestion et à elle seule. Une non-conformité
//    anonyme priverait le RQ de la seule personne capable de préciser le
//    constat ; une suggestion anonyme est au contraire la moitié de ce qu'un
//    canal de ce genre recueille.
//
// Une suggestion n'entre PAS au registre des non-conformités : elle n'a ni
// gravité, ni cause, ni impact sur des résultats, et l'y verser gonflerait un
// compteur que la direction lit comme un indicateur de défaillance.
import React from 'react';
import { useSortieFenetre, AlerteSortie } from './fenetreModale';
import { PastilleNorme } from './PastilleNorme';

/** Ce que l'écran hôte sait de lui-même au moment du clic. Tout est optionnel :
 *  une déclaration depuis un écran sans contexte reste une déclaration valide. */
export interface ContexteNC {
  /** Clé de `NC_SOURCES` déduite de l'écran (equipement, analytique…). */
  source?: string;
  /** Pièce concernée, telle qu'elle s'écrit dans la NC (code équipement, code
   *  document…) — recopiée pour que le RQ retrouve l'objet sans enquêter. */
  docLie?: string;
  equipementCode?: string;
  /** Phrase affichée au déclarant pour qu'il vérifie ce qui sera enregistré.
   *  Sert aussi d'écran d'origine pour une suggestion : elle dit sur quoi porte
   *  la proposition quand le texte reste allusif. */
  libelle?: string;
}

/** Le vocabulaire des SOURCES de non-conformité, tel que le déclarant le lit.
 *
 *  ⚠️ MIROIR de `NC_SOURCES` (`backend/src/api/ged/services/vocabulaire-nc.ts`),
 *  qui fait AUTORITÉ : le moteur refuse toute valeur absente de sa liste
 *  (« Source inconnue : … »). Ajouter une source exige donc de modifier les
 *  DEUX — sinon la fenêtre proposera un choix que l'envoi rejettera.
 *
 *  « À qualifier » est le DÉFAUT et n'est pas un trou : le déclarant décrit,
 *  le RQ classe. Une NC mal rangée est invisible dans la cartographie, alors
 *  qu'une NC explicitement à qualifier se voit et se traite.
 */
const SOURCES_NC: readonly { cle: string; libelle: string }[] = [
  { cle: 'aqualifier', libelle: 'À qualifier — le RQ classera' },
  { cle: 'preanalytique', libelle: 'Pré-analytique' },
  { cle: 'analytique', libelle: 'Analytique' },
  { cle: 'postanalytique', libelle: 'Post-analytique' },
  { cle: 'equipement', libelle: 'Équipement / métrologie' },
  { cle: 'eeq', libelle: 'Évaluation externe (EEQ)' },
  { cle: 'cqi', libelle: 'Contrôle interne (CQI)' },
  { cle: 'reactif', libelle: 'Réactif / consommable' },
  { cle: 'sil', libelle: 'SIL / informatique' },
  { cle: 'environnement', libelle: 'Environnement / locaux' },
  { cle: 'rh', libelle: 'Ressources humaines' },
  { cle: 'documentaire', libelle: 'Documentaire' },
  { cle: 'management', libelle: 'Management de la qualité' },
  { cle: 'reclamation', libelle: 'Réclamation' },
];

/** Les deux canaux, et ce qui les distingue à l'écran. Regroupé ici pour que
 *  l'ajout d'un troisième — s'il en fallait un un jour — ne se fasse pas en
 *  dupliquant la fenêtre. */
type Canal = 'nc' | 'suggestion';

const CANAUX: Record<Canal, {
  chemin: string; bouton: string; titre: string; intro: React.ReactNode;
  labelTitre: string; placeholderTitre: string; labelDetails: string; placeholderDetails: string;
  valider: string; enCours: string; succes: (ref: string) => React.ReactNode;
  anonymat: boolean; couleur: string; encre: string;
  /**
   * L'ARTICLE DE LA NORME QUE CE GESTE SERT — demande de la RQ du 06/09/2026 :
   * « je veux bien une petite icône explicative quand on clique sur déclarer
   * une NC ».
   *
   * ⚠️ Déclarer, ce n'est pas §7.5 « Travail non conforme », qui vise le
   * résultat d'examen produit hors des conditions prévues. Une NC déclarée par
   * la gommette peut porter sur n'importe quoi — un tube mal étiqueté, une
   * porte de congélateur, un courrier égaré. C'est §8.7, la maîtrise des
   * non-conformités du système de management, qui couvre les deux.
   */
  iso: string;
}> = {
  nc: {
    chemin: '/api/nc',
    iso: '8.7',
    bouton: 'Déclarer une NC',
    titre: 'Déclarer une non-conformité',
    intro: (
      <>
        Décrivez ce que vous avez constaté, sans le qualifier : la gravité, le processus et la
        suite à donner sont instruits par le responsable qualité.{' '}
        <strong>Signaler tôt vaut mieux que signaler complet.</strong>
      </>
    ),
    labelTitre: 'Que s’est-il passé ?',
    placeholderTitre: 'Ex. tube hémolysé reçu sans mention sur la fiche',
    labelDetails: 'Détails',
    placeholderDetails: 'Ce que vous avez vu, quand, sur quel poste. Écrivez comme vous le diriez.',
    valider: 'Déclarer',
    enCours: 'Enregistrement…',
    succes: (ref) => (
      <>Non-conformité enregistrée sous la référence <strong>{ref}</strong>. Le responsable
      qualité la prend en charge — vous n’avez rien d’autre à faire.</>
    ),
    anonymat: false,
    couleur: 'var(--amber-500, #E9A23B)',
    encre: '#3B2606',
  },
  suggestion: {
    chemin: '/api/suggestion',
    iso: '8.6',
    bouton: 'Proposer une amélioration',
    titre: 'Proposer une amélioration',
    intro: (
      <>
        Une idée pour que ça marche mieux — même petite, même pas mûre. Elle sera lue et vous
        recevrez une réponse, y compris si elle n’est pas retenue.{' '}
        <strong>Ce n’est pas un signalement d’incident</strong> : pour un écart constaté, la
        déclaration de non-conformité est le bon canal.
      </>
    ),
    labelTitre: 'Que proposez-vous ?',
    placeholderTitre: 'Ex. regrouper les tubes de coagulation sur un seul portoir à la réception',
    labelDetails: 'Détails',
    placeholderDetails: 'Ce que ça changerait, et pourquoi. Écrivez comme vous le diriez.',
    valider: 'Proposer',
    enCours: 'Envoi…',
    succes: (ref) => (
      <>Suggestion enregistrée sous la référence <strong>{ref}</strong>. Elle sera lue et
      recevra une réponse.</>
    ),
    anonymat: true,
    couleur: 'var(--cyan-600, #0B7285)',
    encre: '#FFFFFF',
  },
};

const AMBRE = 'var(--amber-500, #E9A23B)';

/** Une commande flottante AJOUTÉE par l'hôte à la pile (03/10/2026 : le
 *  déstockage d'un aliquot, dans la GED seulement). L'hôte ne la passe QUE si
 *  la personne y a droit : une commande refusée n'est pas grisée, elle est
 *  absente, et la pile se resserre sans trou. */
export interface CommandeFlottante {
  cle: string;
  /** Le libellé montré au survol et au focus — aussi l'`aria-label`. */
  libelle: string;
  /** Infobulle plus longue, facultative. */
  titre?: string;
  icone: React.ReactNode;
  onClick: () => void;
}

/* ── LA PILE DES ACTIONS RAPIDES (RQ, 03/10/2026) ─────────────────────────
 *
 * « Les 3 actions rapides — NC, idée, déstockage — alignées les unes sur les
 * autres, en ne laissant que le rond avec l'icône ; le libellé s'affiche au
 * passage de la souris. »
 *
 *  · UN SEUL CONTENEUR positionné, une colonne flex. Trois `position: fixed`
 *    calculés chacun de leur côté finissent par se chevaucher au quatrième.
 *  · La NC reste EN BAS, à sa place d'avant : c'est la plus grave, on ne
 *    déplace pas un geste que les gens ont dans la main.
 *  · Au repos, un ROND et son icône. Le libellé se déplie vers la gauche au
 *    SURVOL et au FOCUS CLAVIER (`:focus-visible`) — sans ce second cas, une
 *    personne au clavier ne saurait pas sur quoi elle va appuyer.
 *  · Au TOUCHER il n'y a pas de survol : le premier toucher AGIT. Pas de
 *    « premier toucher qui déplie, second qui agit » — deux gestes au lieu
 *    d'un. Les icônes doivent donc parler seules, et `aria-label` + `title`
 *    portent le libellé complet.
 *  · Transition courte, absente sous `prefers-reduced-motion`.
 *
 * Les styles vivent dans une feuille locale (`<style>`) : `:hover` et
 * `:focus-visible` ne s'écrivent pas en style en ligne. Préfixe de classe
 * propre pour ne rien heurter dans les trois modules. */
const CSS_PILE = `
.b24-pile{position:fixed;bottom:22px;z-index:300;display:flex;flex-direction:column;align-items:flex-end;gap:10px;}
.b24-cmd{display:inline-flex;flex-direction:row-reverse;align-items:center;height:46px;min-width:46px;padding:0;border-radius:999px;
  font-family:var(--font-sans);font-size:13px;font-weight:700;cursor:pointer;overflow:hidden;white-space:nowrap;
  box-shadow:0 5px 16px rgba(0,0,0,0.20);transition:box-shadow .12s ease;}
.b24-cmd-ico{flex:none;width:44px;height:44px;display:inline-flex;align-items:center;justify-content:center;}
.b24-cmd-lib{max-width:0;opacity:0;padding-inline-start:0;transition:max-width .16s ease,opacity .12s ease,padding .16s ease;}
.b24-cmd:hover .b24-cmd-lib,.b24-cmd:focus-visible .b24-cmd-lib{max-width:240px;opacity:1;padding-inline-start:16px;}
.b24-cmd:focus-visible{outline:3px solid var(--color-primary,#0B7285);outline-offset:2px;}
@media (prefers-reduced-motion: reduce){.b24-cmd,.b24-cmd-lib{transition:none;}}
`;

export function GommetteNC({ contexte, onDeclaree, commandes = [], formulaireNC }: {
  contexte?: ContexteNC;
  /** Appelé après enregistrement — l'hôte rafraîchit ce qu'il veut. */
  onDeclaree?: (ref: string) => void;
  /** Commandes de l'hôte, posées AU-DESSUS de « Une idée ? » dans la pile. */
  commandes?: CommandeFlottante[];
  /**
   * LE FORMULAIRE DU REGISTRE, injecté par l'hôte (RQ, 05/10/2026).
   *
   * Fourni, c'est LUI que la gommette ouvre : le raccourci et la rubrique
   * « Non-conformités » posent alors exactement les mêmes questions. Absent —
   * le catalogue du manuel, qui n'a pas le magasin de la GED — la fenêtre
   * express ci-dessous reste le seul canal, et c'est mieux que rien.
   *
   * `onFait` est appelé avec la référence quand la déclaration est
   * ENREGISTRÉE, et sans argument quand on renonce : les deux referment la
   * fenêtre, seule la première prévient l'hôte.
   */
  formulaireNC?: (p: { contexte?: ContexteNC; onFait: (ref?: string) => void }) => React.ReactNode;
}) {
  // `ouvert` porte le CANAL ouvert, pas un booléen : les deux gommettes
  // partagent une seule fenêtre, et un booléen aurait exigé un second état pour
  // savoir laquelle on remplit — deux états à tenir d'accord, donc un à oublier.
  const [ouvert, setOuvert] = React.useState<Canal | null>(null);
  const [titre, setTitre] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [anonyme, setAnonyme] = React.useState(false);
  /* ── LE RATTACHEMENT EST UN CHOIX, PAS UNE DÉDUCTION (17/08/2026) ─────────
   *
   * La fenêtre rattachait la NC à l'écran d'où on l'ouvrait, et l'annonçait
   * sans offrir d'en changer (« Sera rattaché à : Équipements »). Or on
   * constate rarement un écart là où on le déclare : on voit une anomalie de
   * réactif en consultant un planning, un défaut documentaire depuis le CQI.
   * Le rattachement était donc faux aussi souvent qu'il était juste, et il
   * n'était pas corrigeable par le déclarant.
   *
   * Il part maintenant à « À qualifier », et l'écran d'origine n'est plus
   * qu'une PROPOSITION à un clic. On ne perd pas la déduction — elle reste
   * offerte — mais elle ne s'applique plus toute seule. */
  const [source, setSource] = React.useState('aqualifier');
  /* La PIÈCE (code équipement, code document) ne suit que si le rattachement
     retenu est bien celui de l'écran : recopier le code d'un automate sur une
     NC que le déclarant vient de reclasser en « documentaire » désignerait un
     objet sans rapport, et le RQ chercherait longtemps. */
  const [garderPiece, setGarderPiece] = React.useState(false);
  const [envoi, setEnvoi] = React.useState(false);

  /* BROUILLON LOCAL (RQ, 19/08/2026) : une déclaration interrompue par un clic
     malheureux se retrouve au retour. Autonome (pas d'import d'un lib d'appli :
     ce composant est PARTAGÉ entre modules) ; clé par canal ; péremption 24 h ;
     purgé à l'envoi réussi. La restauration est PROPOSÉE par bandeau, jamais
     imposée. */
  const cleBrouillon = `bio24.saisie.gommette.${ouvert || ''}`;
  const [brouillon, setBrouillon] = React.useState<{ quand: number; titre: string; description: string } | null>(null);
  /* Ajusté au rendu : la lecture du brouillon est SYNCHRONE, et l'effet faisait
     ouvrir la fenêtre une passe SANS le bandeau de restauration, puis une passe
     avec. Sur une saisie qu'on vient de perdre, ce clignotement se lit comme
     « rien n'a été gardé » — exactement l'inverse de ce que le bandeau annonce. */
  const [ouvertPrec, setOuvertPrec] = React.useState(ouvert);
  if (ouvert !== ouvertPrec) {
    setOuvertPrec(ouvert);
    if (!ouvert) setBrouillon(null);
    else {
      let repris: { quand: number; titre: string; description: string } | null = null;
      try {
        const brut = localStorage.getItem(cleBrouillon);
        const env = brut ? JSON.parse(brut) : null;
        /* `Date.now()` au rendu : c'est une lecture d'horloge, pas un état —
           elle ne rend pas le rendu impur au sens de React (aucun effet de
           bord), et le brouillon est daté à l'écriture, pas ici. */
        if (env && Date.now() - env.quand < 24 * 3600 * 1000 && (env.titre || env.description)) repris = env;
      } catch { /* privé */ }
      setBrouillon(repris);
    }
  }
  React.useEffect(() => {
    if (!ouvert || (!titre && !description)) return undefined;
    const t = setTimeout(() => {
      try { localStorage.setItem(cleBrouillon, JSON.stringify({ quand: Date.now(), titre, description })); } catch { /* privé */ }
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [titre, description, ouvert]);
  const purgerBrouillon = React.useCallback(() => {
    try { localStorage.removeItem(cleBrouillon); } catch { /* privé */ }
    setBrouillon(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleBrouillon]);
  const [erreur, setErreur] = React.useState('');
  const [succes, setSucces] = React.useState('');
  const champRef = React.useRef<HTMLInputElement | null>(null);
  const c = ouvert ? CANAUX[ouvert] : null;

  /* ⚠️ Échap passait DIRECTEMENT par `setOuvert(null)` : une déclaration de
     non-conformité à moitié rédigée disparaissait sans un mot. Il emprunte
     désormais la porte unique — celle qui pose l'alerte (règle du 04/09/2026,
     voir fenetreModale.tsx). Cette fenêtre n'ayant pas de croix, Échap et
     « Annuler » sont ses deux sorties : Échap est celle qu'on peut faire sans
     le vouloir, c'est donc elle qui doit demander. */
  const boiteGommette = React.useRef<HTMLDivElement | null>(null);
  const sortie = useSortieFenetre({
    boite: boiteGommette,
    onClose: () => setOuvert(null),
    actif: !!ouvert && !envoi,
  });

  // Le focus va sur le premier champ à l'ouverture : sans cela, au clavier, il
  // reste sur la gommette qui vient de disparaître derrière la fenêtre.
  React.useEffect(() => { if (ouvert) champRef.current?.focus(); }, [ouvert]);

  const fermer = () => {
    if (envoi) return;
    setOuvert(null); setErreur(''); setSucces('');
  };

  const ouvrir = (canal: Canal) => {
    setOuvert(canal); setErreur(''); setSucces('');
    setTitre(''); setDescription(''); setAnonyme(false);
    // Jamais pré-rempli par l'écran : c'est tout l'objet du changement.
    setSource('aqualifier'); setGarderPiece(false);
  };

  const envoyer = async () => {
    if (!titre.trim() || envoi || !ouvert) return;
    setEnvoi(true); setErreur('');
    try {
      const corps = ouvert === 'suggestion'
        ? {
          titre: titre.trim(),
          description: description.trim(),
          anonyme,
          // L'écran d'origine dit sur quoi porte la suggestion quand le texte
          // reste allusif — « ce serait mieux à l'envers » ne veut rien dire
          // sans savoir d'où c'est parti.
          origineEcran: contexte?.libelle || undefined,
        }
        : {
          titre: titre.trim(),
          description: description.trim(),
          // `aqualifier` et non un processus deviné : une NC mal classée est
          // invisible dans la cartographie, alors qu'une NC explicitement à
          // qualifier se voit et se traite.
          source,
          docLie: garderPiece ? (contexte?.docLie || undefined) : undefined,
          equipementCode: garderPiece ? (contexte?.equipementCode || undefined) : undefined,
        };
      const r = await fetch(CANAUX[ouvert].chemin, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(corps),
      });
      const data = await r.json().catch(() => null);
      if (!r.ok) {
        setErreur(data?.error?.message || data?.error
          || 'L’envoi n’a pas pu être enregistré.');
        return;
      }
      const ref = data?.nc?.ref || data?.suggestion?.ref || data?.ref || '';
      /* ⚠️ C'EST PARTI — la garde de sortie doit l'apprendre. Cette fenêtre ne
         se referme pas en envoyant : elle montre l'écran de confirmation, et
         « Déclarer autre chose » y rouvre un formulaire vide. Sans cet oubli,
         Échap posait ensuite « des modifications ne seront pas enregistrées »
         sur une déclaration DÉJÀ enregistrée, référence à l'appui. */
      sortie.saisieEnregistree();
      setSucces(ref);
      setTitre(''); setDescription('');
      purgerBrouillon();
      onDeclaree?.(ref);
    } catch {
      setErreur('Serveur injoignable — rien n’a été enregistré.');
    } finally {
      setEnvoi(false);
    }
  };

  if (!ouvert || !c) {
    // UNE PILE verticale, de haut en bas : commandes de l'hôte, idée, NC. La
    // NC reste en bas et la plus marquée (ambre) : c'est le canal dont le
    // silence se paie en récidive. Voir `CSS_PILE` pour le pourquoi du rond.
    const ideeIcone = (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M9 18h6M10 22h4" />
        <path d="M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2Z" />
      </svg>
    );
    const ncIcone = (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 9v4M12 17h.01" />
        <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
      </svg>
    );
    const blanc: React.CSSProperties = { background: '#fff', color: 'var(--cyan-700, #0B6E80)', border: '1.5px solid var(--cyan-600, #0B7285)' };
    return (
      <div className="b24-pile" style={{
        /* Ancrée au CONTENU, pas à la fenêtre.
         *
         * Mesuré le 18/08 sur une fenêtre de 2400 px : la colonne de contenu
         * fait 1280 px et s'arrête à 1840 px, tandis que la commande flottante
         * restait à 22 px du bord de la fenêtre — quatre cents pixels plus
         * loin, seule au milieu d'une marge vide. C'est ce qu'on voit en
         * dézoomant : le contenu se resserre au centre, les commandes
         * flottantes partent aux coins et la page paraît déséquilibrée.
         *
         * `max()` fait les deux cas d'un coup : tant que la fenêtre est plus
         * étroite que la colonne, la marge calculée devient négative et c'est
         * le retrait fixe qui gagne. Aucune mesure en JavaScript, donc rien à
         * recalculer au zoom — le navigateur s'en charge. */
        insetInlineEnd: 'max(22px, calc((100vw - var(--container-xl)) / 2 + 22px))',
        /* ⚠️ 300 = `COUCHES.FLOTTANT`, et il doit rester SOUS `COUCHES.MODALE`
           (400). Ce fichier est partagé par les trois modules et ne peut pas
           importer la table de la GED ; le nombre est donc écrit en clair, et
           `backend/tests/qualite-couches.test.js` vérifie qu'il n'a pas dérivé.
           Le monter au-dessus d'une fenêtre rendrait ce bouton cliquable
           par-dessus le voile d'une saisie en cours — on ouvrirait une seconde
           déclaration sans savoir laquelle on remplit (relevé MAJ-13). */
        bottom: 22, zIndex: 300,
      }}>
        <style>{CSS_PILE}</style>
        {commandes.map((cmd) => (
          <button key={cmd.cle} type="button" className="b24-cmd" onClick={cmd.onClick}
            aria-label={cmd.libelle} title={cmd.titre || cmd.libelle} style={blanc}>
            <span className="b24-cmd-ico">{cmd.icone}</span>
            <span className="b24-cmd-lib">{cmd.libelle}</span>
          </button>
        ))}
        <button type="button" className="b24-cmd" onClick={() => ouvrir('suggestion')}
          aria-label="Proposer une amélioration"
          title="Proposer une amélioration (§8.6 — retour du personnel)" style={blanc}>
          <span className="b24-cmd-ico">{ideeIcone}</span>
          <span className="b24-cmd-lib">Une idée ?</span>
        </button>
        <button type="button" className="b24-cmd" onClick={() => ouvrir('nc')}
          aria-label="Déclarer une non-conformité" title="Déclarer une non-conformité"
          style={{ background: AMBRE, color: '#3B2606', border: 'none', boxShadow: '0 6px 20px rgba(0,0,0,0.22)' }}>
          <span className="b24-cmd-ico">{ncIcone}</span>
          <span className="b24-cmd-lib">Déclarer une NC</span>
        </button>
      </div>
    );
  }

  /* ── LE RACCOURCI OUVRE LE FORMULAIRE DU REGISTRE (RQ, 05/10/2026) ───────
   *
   * « Le bouton déclarer une NC est un raccourci vers déclarer une NC à partir
   * de la rubrique NC. Oublier le popup actuel et le remplacer par le contenu
   * de la rubrique en pop-up. »
   *
   * Les deux champs de la fenêtre express étaient un pari : déclarer en vingt
   * secondes, le RQ qualifie ensuite. Il se payait ailleurs — la liste des
   * problèmes du secteur (les critères d'acceptation d'échantillon pour le
   * pré-analytique), la gravité, les résultats patients concernés ne sont
   * demandés qu'au registre. Le raccourci, qui est le point d'entrée le plus
   * utilisé, produisait donc des déclarations plus pauvres que le même geste
   * fait deux écrans plus loin. Un seul formulaire, à deux endroits.
   *
   * La fenêtre ne porte PAS d'en-tête à elle : le formulaire injecté a son
   * titre et son rappel de norme. Deux titres l'un sur l'autre se lisent comme
   * deux fenêtres empilées.
   */
  if (ouvert === 'nc' && formulaireNC) {
    return (
      <div role="dialog" aria-modal="true" aria-label={c.titre}
        {...sortie.proprietesVoile}
        style={{ position: 'fixed', inset: 0, zIndex: 320,
          background: 'rgba(11,20,38,0.45)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', padding: 20 }}>
        {/* TAILLE FIXE (RQ, 13/10/2026 : « que la fenêtre garde toujours la même
            taille, peu importe le contenu »). Les étapes du formulaire n'ont pas
            la même longueur : la fenêtre ne bouge plus de l'une à l'autre, c'est
            son CONTENU qui défile. La hauteur reste bornée par l'écran, pour
            qu'un portable atteigne toujours le bas. */}
        <div ref={boiteGommette} {...sortie.proprietesBoite}
          style={{ width: '100%', maxWidth: 900, height: 'min(860px, calc(100vh - 40px))', overflowY: 'auto', background: '#fff', borderRadius: 14,
            boxShadow: '0 24px 60px rgba(0,0,0,0.3)', position: 'relative', ...sortie.proprietesBoite.style }}>
          {sortie.alerte && <AlerteSortie onRester={sortie.rester} onQuitter={sortie.quitterSansEnregistrer} />}
          {formulaireNC({
            contexte,
            onFait: (ref) => {
              /* ⚠️ La garde de sortie doit apprendre que c'est enregistré :
                 sans cela, la fermeture poserait « des modifications ne seront
                 pas enregistrées » sur une NC qui porte déjà sa référence. */
              if (ref) { sortie.saisieEnregistree(); onDeclaree?.(ref); }
              setOuvert(null);
            },
          })}
        </div>
      </div>
    );
  }

  const principal: React.CSSProperties = { ...btnPrincipal, background: c.couleur, color: c.encre };

  return (
    /* ⚠️ Le voile ne ferme RIEN (règle du 04/09/2026, voir fenetreModale.tsx).
       Une déclaration de non-conformité à moitié rédigée est ce qu'un clic à
       côté emportait — et la gommette est justement le point d'entrée le plus
       utilisé du signalement. */
    <div role="dialog" aria-modal="true" aria-label={c.titre}
      {...sortie.proprietesVoile}
      style={{ position: 'fixed', inset: 0, zIndex: 320,
        /* Même teinte de voile que `VOILE` dans `ged/src/components/ui.tsx`
           (24/08/2026). Recopiée et non importée : le socle est autonome,
           servi tel quel aux trois apps. Si l'une bouge, bouger l'autre. */
        background: 'rgba(11,20,38,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div ref={boiteGommette} {...sortie.proprietesBoite}
        style={{ width: '100%', maxWidth: 520, background: '#fff', borderRadius: 14, boxShadow: '0 24px 60px rgba(0,0,0,0.3)', overflow: 'hidden', position: 'relative', ...sortie.proprietesBoite.style }}>
        {sortie.alerte && <AlerteSortie onRester={sortie.rester} onQuitter={sortie.quitterSansEnregistrer} />}

        <div style={{ padding: '18px 22px 14px', borderBottom: '1px solid var(--border-default, #E4E7EC)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: c.couleur, flex: 'none' }} />
            <h2 style={{ margin: 0, fontSize: 17.5, fontWeight: 700, color: 'var(--text-strong, #101828)' }}>{c.titre}</h2>
            {/* « Pourquoi fait-on ça ? », à l'endroit où on le fait. Discrète :
                un utilisateur aguerri n'a pas à subir la pédagogie du débutant,
                mais elle reste à un clic. */}
            <PastilleNorme art={c.iso} />
          </div>
          <p style={{ margin: '7px 0 0', fontSize: 12.5, color: 'var(--text-muted, #667085)', lineHeight: 1.5 }}>
            {c.intro}
          </p>
        </div>

        {succes ? (
          // Fin de course explicite : le déposant doit repartir avec la preuve
          // que son envoi existe — une référence, pas un message qui s'efface.
          <div style={{ padding: '22px', display: 'grid', gap: 14, justifyItems: 'start' }}>
            <div style={{ fontSize: 13.5, color: 'var(--text-body, #475467)', lineHeight: 1.55 }}>
              {c.succes(succes)}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => { setSucces(''); }} style={btnSecondaire}>
                {ouvert === 'suggestion' ? 'Proposer autre chose' : 'Déclarer autre chose'}
              </button>
              <button onClick={() => { setSucces(''); setOuvert(null); }} style={principal}>Terminé</button>
            </div>
          </div>
        ) : (
          <div style={{ padding: '18px 22px 20px', display: 'grid', gap: 14 }}>
            <div>
              <label htmlFor="nc-express-titre" style={lbl}>{c.labelTitre}</label>
              {brouillon && !titre && !description && (
                <div role="status" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '7px 10px', borderRadius: 8, fontSize: 12, background: 'var(--cyan-50, #ECFEFF)', border: '1px solid var(--cyan-200, #A5F3FC)', marginBottom: 8 }}>
                  <span>
                    Une saisie non envoyée de {new Date(brouillon.quand).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} a été retrouvée sur ce poste.
                  </span>
                  <button type="button" onClick={() => { setTitre(brouillon.titre); setDescription(brouillon.description); setBrouillon(null); }}
                    style={{ fontSize: 12, fontWeight: 600, cursor: 'pointer', border: '1px solid var(--cyan-200, #A5F3FC)', borderRadius: 6, padding: '3px 9px', background: '#fff' }}>
                    Reprendre
                  </button>
                  <button type="button" onClick={purgerBrouillon}
                    style={{ fontSize: 12, cursor: 'pointer', border: 'none', background: 'none', color: 'var(--text-muted, #667)', textDecoration: 'underline' }}>
                    Écarter
                  </button>
                </div>
              )}
              <input id="nc-express-titre" ref={champRef} value={titre} maxLength={160}
                onChange={(e) => { setTitre(e.target.value); if (erreur) setErreur(''); }}
                placeholder={c.placeholderTitre}
                style={champ} />
            </div>
            <div>
              <label htmlFor="nc-express-desc" style={lbl}>{c.labelDetails} <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(facultatif)</span></label>
              <textarea id="nc-express-desc" value={description} rows={4}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={c.placeholderDetails}
                style={{ ...champ, height: 'auto', padding: '9px 12px', resize: 'vertical' }} />
            </div>

            {/* L'anonymat n'est offert QU'À la suggestion. Une non-conformité
                anonyme priverait le RQ de la seule personne capable de préciser
                le constat ; une suggestion anonyme est au contraire la moitié
                de ce qu'un canal de ce genre recueille. */}
            {c.anonymat && (
              <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12.5, color: 'var(--text-body, #475467)', lineHeight: 1.45 }}>
                <input type="checkbox" checked={anonyme} onChange={(e) => setAnonyme(e.target.checked)} style={{ marginTop: 2 }} />
                <span>
                  Déposer anonymement — <span style={{ color: 'var(--text-muted, #667085)' }}>votre nom
                  n’apparaîtra ni sur la suggestion, ni dans la réponse, ni en revue de direction.</span>
                </span>
              </label>
            )}

            {/* SUGGESTION — l'écran d'origine reste une simple mention : elle
                situe la proposition, elle ne classe rien. */}
            {ouvert === 'suggestion' && contexte?.libelle && (
              <div style={{ fontSize: 12, color: 'var(--text-muted, #667085)', background: 'var(--surface-sunken, #F7F8FA)', borderRadius: 8, padding: '9px 12px', lineHeight: 1.45 }}>
                Déposée depuis : <strong style={{ color: 'var(--text-strong, #101828)' }}>{contexte.libelle}</strong>
              </div>
            )}

            {/* NON-CONFORMITÉ — le rattachement se CHOISIT. */}
            {ouvert === 'nc' && (
              <div style={{ display: 'grid', gap: 8 }}>
                <label style={{ display: 'grid', gap: 5, fontSize: 12.5, fontWeight: 600, color: 'var(--text-body, #475467)' }}>
                  <span>Rattachement <span style={{ fontWeight: 400, color: 'var(--text-muted, #667085)' }}>· facultatif</span></span>
                  <select value={source} onChange={(e) => { setSource(e.target.value); if (e.target.value !== contexte?.source) setGarderPiece(false); }}
                    style={{ fontFamily: 'var(--font-sans)', fontSize: 13, padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border-default, #D0D5DD)', background: '#fff', color: 'var(--text-strong, #101828)' }}>
                    {SOURCES_NC.map((o) => <option key={o.cle} value={o.cle}>{o.libelle}</option>)}
                  </select>
                </label>

                {/* La déduction de l'écran, OFFERTE et non appliquée. Un clic
                    suffit à la retenir ; sans clic, elle n'engage rien. */}
                {contexte?.source && contexte.source !== 'aqualifier' && source !== contexte.source && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted, #667085)', background: 'var(--surface-sunken, #F7F8FA)', borderRadius: 8, padding: '9px 12px', lineHeight: 1.5 }}>
                    Vous déclarez depuis <strong style={{ color: 'var(--text-strong, #101828)' }}>{contexte.libelle || contexte.source}</strong>.
                    {' '}
                    <button type="button"
                      onClick={() => { setSource(contexte.source as string); setGarderPiece(!!(contexte.docLie || contexte.equipementCode)); }}
                      className="lien-doux"
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'var(--font-sans)', fontSize: 12, fontWeight: 700, color: 'var(--color-primary-active, #0B6FA4)' }}>
                      Rattacher à cet écran
                    </button>
                  </div>
                )}

                {/* La pièce précise ne se joint que si le rattachement est bien
                    celui de l'écran — sinon elle désignerait un objet sans
                    rapport avec la source retenue. */}
                {source === contexte?.source && (contexte?.docLie || contexte?.equipementCode) && (
                  <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12.5, color: 'var(--text-body, #475467)', lineHeight: 1.45 }}>
                    <input type="checkbox" checked={garderPiece} onChange={(e) => setGarderPiece(e.target.checked)} style={{ marginTop: 2 }} />
                    <span>
                      Joindre la pièce concernée — <strong style={{ color: 'var(--text-strong, #101828)' }}>{contexte.equipementCode || contexte.docLie}</strong>
                    </span>
                  </label>
                )}
              </div>
            )}

            {erreur && (
              <div role="alert" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--red-600, #C0362C)', background: 'var(--red-50, #FDF2F1)', borderRadius: 8, padding: '9px 12px' }}>
                {erreur}
              </div>
            )}

            {/* Ordre normé du 10/08/2026 : « Annuler » puis l'action, à droite. */}
            <div style={{ display: 'flex', gap: 8, marginTop: 2, justifyContent: 'flex-end' }}>
              <button onClick={fermer} disabled={envoi} style={btnSecondaire}>Annuler</button>
              <button onClick={() => void envoyer()} disabled={!titre.trim() || envoi}
                style={{ ...principal, opacity: titre.trim() && !envoi ? 1 : 0.55, cursor: titre.trim() && !envoi ? 'pointer' : 'not-allowed' }}>
                {envoi ? c.enCours : c.valider}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const lbl: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '0.04em', color: 'var(--text-muted, #667085)', marginBottom: 6,
};
const champ: React.CSSProperties = {
  width: '100%', height: 40, border: '1.5px solid var(--border-default, #E4E7EC)', borderRadius: 8,
  padding: '0 12px', fontSize: 13.5, color: 'var(--text-strong, #101828)', background: '#fff',
  fontFamily: 'var(--font-sans)',
};
const btnPrincipal: React.CSSProperties = {
  fontFamily: 'var(--font-sans)', fontSize: 13.5, fontWeight: 700, padding: '9px 18px',
  borderRadius: 8, border: 'none', background: AMBRE, color: '#3B2606', cursor: 'pointer',
};
const btnSecondaire: React.CSSProperties = {
  fontFamily: 'var(--font-sans)', fontSize: 13.5, fontWeight: 600, padding: '9px 16px',
  borderRadius: 8, border: '1px solid var(--border-default, #E4E7EC)',
  background: 'var(--surface-sunken, #F7F8FA)', color: 'var(--text-body, #475467)', cursor: 'pointer',
};
